import { ref, computed, type Ref } from "vue";
import type { AuthenticationStatus, PackTask, PlatformInfo } from "../types";

export interface PlatformRequirement {
	codeName: string;
	productName: string;
	usedBy: string[];
	authStatus: string;
	requiresAuth: boolean;
}

export type { AuthenticationStatus, PackTask, PlatformInfo } from "../types";

export interface UsePackRequirementsOptions {
	loadedPackTasks: Ref<PackTask[]>;
	getOrganizationId: () => string;
	/** Get platform login URL (opens in popup) */
	getPlatformLoginUrl?: (codeName: string, redirectUri: string, organizationId?: string) => Promise<string>;
	/** Logout from a platform */
	logoutPlatform?: (codeName: string, organizationId?: string) => Promise<{ success: boolean }>;
	/** Fetch available platforms with auth status */
	/**
	 * `allowStale` asks Scribe for the caller's last good list when Exchange is unavailable
	 * (the response then carries `stale: true`). Only the passive pre-flight load sets it; the
	 * post-login status check never does, because a stale copy would report the login failed.
	 */
	fetchPlatforms?: (options?: { allowStale?: boolean }) => Promise<{ platforms: PlatformInfo[]; stale?: boolean }>;
}

export function usePackRequirements(options: UsePackRequirementsOptions) {
	const { loadedPackTasks, getOrganizationId, getPlatformLoginUrl, logoutPlatform, fetchPlatforms } = options;

	const platformRequirements = ref<PlatformRequirement[]>([]);
	const platformBusy = ref<Record<string, boolean>>({});
	const platformErrors = ref<Record<string, string>>({});
	const platformLoginPending = ref<Record<string, boolean>>({});
	type LoginAttempt = { cancel: () => void; closePopup?: () => void; reconcileClosedPopup?: () => boolean };
	const activeLogins = new Map<string, LoginAttempt>();
	const platformsLoaded = ref(false);
	// True while allPlatformsList is Scribe's last good copy rather than a live answer (LSP-23042).
	const platformsStale = ref(false);
	const allPlatformsList = ref<PlatformInfo[]>([]);
	// One shared in-flight platform-list fetch: concurrent passive loads (e.g. rapid pack
	// re-selection) await the SAME request instead of each firing its own. Cleared once settled so a
	// later call still retries — a failed load is NOT latched (a transient blip must be able to
	// recover, and admin's pack-switch is a legitimate retry). The dedup is what prevents the
	// overlapping-callers fetch storm; no failure latch is needed on top of it.
	let platformsLoadPromise: Promise<void> | null = null;
	let latestPlatformRequest: Promise<void> | null = null;
	let releasePlatformsLoadWaiters: (() => void) | null = null;
	let platformsLoadGeneration = 0;
	let platformsRequestSequence = 0;
	// The live (no allowStale) refresh in flight, if any. The newest request owns the status, so a
	// passive load issued meanwhile supersedes it; it must then ask for a live answer too, or a
	// stale one would replace the live refresh and report "We couldn't log in" after a login.
	let liveRefreshInFlight: Promise<void> | null = null;
	let liveRefreshToken: symbol | null = null;

	const hasPlatformRequirements = computed(() => {
		return platformRequirements.value.some((p) => p.requiresAuth);
	});

	const allPlatformsAuthenticated = computed(() => {
		const requiredPlatforms = platformRequirements.value.filter((p) => p.requiresAuth);
		return requiredPlatforms.length === 0 || (platformsLoaded.value && requiredPlatforms.every((p) => p.authStatus === "Authenticated"));
	});

	const computePlatformRequirements = (tasks: PackTask[]) => {
		const platformMap = new Map<string, { usedBy: Set<string> }>();

		for (const task of tasks) {
			if (task.type === "platform" && task.platform) {
				if (!platformMap.has(task.platform)) {
					platformMap.set(task.platform, { usedBy: new Set() });
				}
				platformMap.get(task.platform)!.usedBy.add(task.name);
			}

			// A user-input field can also require a platform login when it sources its options
			// from a platform MCP tool (field.mcpDataSource.platformCodeName). Without this, the
			// field silently fails to load options ("Failed to load options") when that platform
			// is not authenticated — the contact-admin run/queue flow already gated on it, so the
			// realtime (async/live) flow must too, or the two diverge. See useRunAgentPlatforms.
			if (task.type === "user-input" && task.inlineFormConfig?.fields) {
				for (const field of task.inlineFormConfig.fields) {
					const platformCodeName = field.mcpDataSource?.platformCodeName;
					if (platformCodeName) {
						if (!platformMap.has(platformCodeName)) {
							platformMap.set(platformCodeName, { usedBy: new Set() });
						}
						platformMap.get(platformCodeName)!.usedBy.add(`${field.label || field.key} field`);
					}
				}
			}
		}

		const requirements: PlatformRequirement[] = [];
		for (const [codeName, data] of platformMap) {
			const platformInfo = allPlatformsList.value.find((p) => p.codeName === codeName);
			const listedStatus = platformInfo?.authenticationStatus || "NotAuthenticated";
			// A stale list may say a platform needs no login (fixed by the platform) but never that the
			// user is logged in: their connection can have changed since. Treat it as logged out so the
			// login step and the run gate behave as they do when the list can't load at all.
			const authStatus = platformsStale.value && listedStatus !== "NoAuthRequired" ? "NotAuthenticated" : listedStatus;
			const requiresAuth = authStatus !== "NoAuthRequired";
			if (authStatus === "Authenticated" || !requiresAuth) {
				delete platformErrors.value[codeName];
			}

			requirements.push({
				codeName,
				productName: platformInfo?.productName || codeName,
				usedBy: Array.from(data.usedBy),
				authStatus,
				requiresAuth,
			});
		}

		platformRequirements.value = requirements;
	};

	const loadPlatformsIfNeeded = async () => {
		// A stale list doesn't count as loaded: the next passive load tries for a live one.
		if ((platformsLoaded.value && !platformsStale.value) || !fetchPlatforms) return;
		// Share one in-flight fetch so concurrent callers await the SAME request instead of each
		// firing its own (dedup — safe for every consumer).
		if (!platformsLoadPromise) {
			const loadGeneration = platformsLoadGeneration;
			const loadRequestSequence = ++platformsRequestSequence;
			const allowStale = liveRefreshInFlight === null;
			let resolvePlatformsLoad!: () => void;
			const loadPromise = new Promise<void>((resolve) => {
				resolvePlatformsLoad = resolve;
			});
			platformsLoadPromise = loadPromise;
			latestPlatformRequest = loadPromise;
			releasePlatformsLoadWaiters = () => {
				resolvePlatformsLoad();
				if (platformsLoadPromise === loadPromise) {
					platformsLoadPromise = null;
					releasePlatformsLoadWaiters = null;
				}
			};
			void (async () => {
				try {
					const response = await (allowStale ? fetchPlatforms({ allowStale: true }) : fetchPlatforms());
					if (loadGeneration !== platformsLoadGeneration || loadRequestSequence !== platformsRequestSequence) return;
					platformsStale.value = response.stale === true;
					allPlatformsList.value = response.platforms || [];
					platformsLoaded.value = true;

					// Recompute requirements with fresh data
					if (loadedPackTasks.value.length > 0) {
						computePlatformRequirements(loadedPackTasks.value);
					}
				} catch (error) {
					// Pre-flight only — callers without platform tasks don't read this list, so a
					// failure here must not block the flow.
					console.error("Failed to load platforms:", error);
				} finally {
					if (loadGeneration === platformsLoadGeneration && loadRequestSequence === platformsRequestSequence) {
						releasePlatformsLoadWaiters?.();
					}
				}
			})();
		}
		await platformsLoadPromise;
	};

	const refreshPlatformAuthStatus = () => {
		if (!fetchPlatforms) return Promise.resolve();
		const refreshGeneration = platformsLoadGeneration;
		const refreshRequestSequence = ++platformsRequestSequence;
		const refreshToken = Symbol("live-platform-refresh");
		liveRefreshToken = refreshToken;
		platformsLoaded.value = false;
		const refreshRequest = (async () => {
			try {
				const response = await fetchPlatforms();
				if (refreshGeneration !== platformsLoadGeneration || refreshRequestSequence !== platformsRequestSequence) return;
				platformsStale.value = response.stale === true;
				allPlatformsList.value = response.platforms || [];
				platformsLoaded.value = true;
				if (loadedPackTasks.value.length > 0) {
					computePlatformRequirements(loadedPackTasks.value);
				}
			} catch (error) {
				console.error("Failed to refresh platform auth status:", error);
			} finally {
				if (refreshGeneration === platformsLoadGeneration && refreshRequestSequence === platformsRequestSequence) {
					releasePlatformsLoadWaiters?.();
				}
				// Token, not the promise: a fetch that throws synchronously settles before
				// refreshRequest is assigned, and the flag must still clear.
				if (liveRefreshToken === refreshToken) {
					liveRefreshToken = null;
					liveRefreshInFlight = null;
				}
			}
		})();
		if (liveRefreshToken === refreshToken) {
			liveRefreshInFlight = refreshRequest;
		}
		latestPlatformRequest = refreshRequest;
		return refreshRequest;
	};

	type PopupOutcome = "completed" | "closed" | "blocked" | "timeout" | "cancelled";

	const openLoginPopup = (url: string, codeName: string, attempt: LoginAttempt): Promise<PopupOutcome> => {
		return new Promise((resolve) => {
			// Each attempt owns its window; a named shared target lets another platform's
			// login reuse it and an older attempt's Cancel close the newer login.
			const popup = window.open(url, "_blank", "width=600,height=700,left=200,top=200");
			if (!popup) {
				resolve("blocked");
				return;
			}
			let settled = false;
			let pollTimer: ReturnType<typeof setTimeout>;
			const deadline = Date.now() + 300000; // Allow five minutes for an interactive login.
			const finish = (outcome: PopupOutcome) => {
				if (settled) return;
				settled = true;
				clearTimeout(pollTimer);
				window.removeEventListener("message", messageHandler);
				attempt.closePopup = undefined;
				attempt.reconcileClosedPopup = undefined;
				try { popup.close(); } catch { console.warn("[platform-login] Popup close unavailable", { codeName }); }
				resolve(outcome);
			};
			const messageHandler = (event: MessageEvent) => {
				if (event.data?.type !== "platform-auth-complete") return;
				if (event.origin !== window.location.origin || event.source !== popup || event.data.platform !== codeName) {
					console.warn("[platform-login] Ignored unrelated completion", { codeName });
					return;
				}
				// Completion is only a signal to refresh. The server owns authentication status.
				finish("completed");
			};
			const checkClosed = () => {
				if (popup.closed) return finish("closed");
				if (Date.now() >= deadline) return finish("timeout");
				pollTimer = setTimeout(checkClosed, 2000);
			};
			attempt.closePopup = () => finish("cancelled");
			attempt.reconcileClosedPopup = () => {
				if (!popup.closed) return false;
				finish("closed");
				return true;
			};
			window.addEventListener("message", messageHandler);
			pollTimer = setTimeout(checkClosed, 2000);
		});
	};

	const cancelPlatformLogin = (codeName: string) => {
		const attempt = activeLogins.get(codeName);
		if (!attempt) return;
		// A physical close can precede the close poll. Preserve its normal status check
		// instead of relabelling an already-finished interaction as a cancellation.
		if (attempt.reconcileClosedPopup?.()) return;
		activeLogins.delete(codeName);
		attempt.cancel();
		attempt.closePopup?.();
		platformBusy.value = { ...platformBusy.value, [codeName]: false };
		platformLoginPending.value = { ...platformLoginPending.value, [codeName]: false };
		platformErrors.value = { ...platformErrors.value, [codeName]: "Login was cancelled. Please try again." };
		console.warn("[platform-login] Cancelled", { codeName, generation: platformsLoadGeneration });
	};

	const loginPlatform = async (codeName: string) => {
		if (platformBusy.value[codeName]) {
			console.warn("[platform-login] Already in progress", { codeName });
			return;
		}
		if (!getPlatformLoginUrl) {
			platformErrors.value = { ...platformErrors.value, [codeName]: "Login is unavailable. Please try again later." };
			console.warn("[platform-login] Missing login transport", { codeName });
			return;
		}
		const loginGeneration = platformsLoadGeneration;
		let cancel!: () => void;
		const cancelled = new Promise<undefined>((resolve) => { cancel = () => resolve(undefined); });
		const attempt: LoginAttempt = { cancel };
		activeLogins.set(codeName, attempt);
		const ownsAttempt = () => loginGeneration === platformsLoadGeneration && activeLogins.get(codeName) === attempt;
		const setError = (message: string, reason: string) => {
			if (!ownsAttempt()) return;
			platformErrors.value = { ...platformErrors.value, [codeName]: message };
			console.warn("[platform-login] Login incomplete", { codeName, reason, generation: loginGeneration });
		};
		platformBusy.value = { ...platformBusy.value, [codeName]: true };
		platformLoginPending.value = { ...platformLoginPending.value, [codeName]: true };
		platformErrors.value = { ...platformErrors.value, [codeName]: "" };
		try {
			const redirectUri = `${window.location.origin}/platform-callback.html?platform=${encodeURIComponent(codeName)}&success=true`;
			const loginUrl = await Promise.race([getPlatformLoginUrl(codeName, redirectUri, getOrganizationId()), cancelled]);
			if (!ownsAttempt() || !loginUrl) return;
			const outcome = await openLoginPopup(loginUrl, codeName, attempt);
			if (!ownsAttempt()) {
				// Spark reselection detaches an already-open login, but its successful
				// completion can still refresh the replacement Spark's shared platform status.
				// Explicit cancellation and a newer login must never take this path.
				if (loginGeneration !== platformsLoadGeneration && (outcome === "completed" || outcome === "closed")) {
					await new Promise(resolve => setTimeout(resolve, 500));
					if (!activeLogins.has(codeName) && (platformsLoadPromise || platformRequirements.value.some(platform => platform.codeName === codeName))) {
						await refreshPlatformAuthStatus();
					}
				}
				return;
			}
			// The provider interaction is over. Keep Login busy through the status check,
			// but stop offering Cancel so a completed login cannot skip reconciliation.
			platformLoginPending.value = { ...platformLoginPending.value, [codeName]: false };
			if (outcome === "blocked") {
				setError("Your browser blocked the login window. Allow pop-ups and try again.", outcome);
				return;
			}
			if (outcome === "timeout") {
				setError("Login timed out. Please try again.", outcome);
				return;
			}
			await Promise.race([new Promise(resolve => setTimeout(resolve, 500)), cancelled]);
			if (!ownsAttempt()) return;
			await Promise.race([refreshPlatformAuthStatus(), cancelled]);
			// A newer refresh owns the status result. Wait for that request before treating
			// platformsLoaded=false as failure; cancellation still releases this attempt.
			let observedRequest: Promise<void> | null;
			do {
				if (!ownsAttempt()) return;
				observedRequest = latestPlatformRequest;
				await Promise.race([observedRequest, cancelled]);
			} while (observedRequest !== latestPlatformRequest);
			if (!ownsAttempt()) return;
			if (!platformsLoaded.value) {
				setError("We couldn't check your login status. Please try again.", "status-unavailable");
				return;
			}
			const platform = platformRequirements.value.find(requirement => requirement.codeName === codeName);
			if (platform?.authStatus !== "Authenticated" && platform?.authStatus !== "NoAuthRequired") {
				setError(`We couldn't log in to ${platform?.productName || codeName}. Please try again.`, "not-authenticated");
			}
		} catch {
			// Provider errors may contain credentials or URLs. Log only our bounded reason/identity.
			setError("We couldn't start login. Please try again.", "login-start-failed");
		} finally {
			attempt.closePopup?.();
			if (ownsAttempt()) {
				activeLogins.delete(codeName);
				platformBusy.value = { ...platformBusy.value, [codeName]: false };
				platformLoginPending.value = { ...platformLoginPending.value, [codeName]: false };
			}
		}
	};

	const handleLogoutPlatform = async (codeName: string) => {
		if (!logoutPlatform || platformBusy.value[codeName]) return;
		platformErrors.value = { ...platformErrors.value, [codeName]: "" };
		const logoutGeneration = platformsLoadGeneration;
		platformBusy.value = { ...platformBusy.value, [codeName]: true };
		try {
			await logoutPlatform(codeName, getOrganizationId());
			await refreshPlatformAuthStatus();
		} catch (error) {
			console.error("Logout failed for", codeName, error);
			await refreshPlatformAuthStatus();
		} finally {
			if (logoutGeneration === platformsLoadGeneration) platformBusy.value = { ...platformBusy.value, [codeName]: false };
		}
	};

	const getPlatformAuthIcon = (req: PlatformRequirement): string => {
		switch (req.authStatus) {
			case "Authenticated":
				return "mdi-check-circle";
			case "AuthenticatedButFailing":
				return "mdi-alert-circle";
			default:
				return "mdi-close-circle";
		}
	};

	const getPlatformAuthColor = (req: PlatformRequirement): string => {
		switch (req.authStatus) {
			case "Authenticated":
				return "success";
			case "AuthenticatedButFailing":
				return "warning";
			default:
				return "error";
		}
	};

	const getPlatformAuthLabel = (req: PlatformRequirement): string => {
		switch (req.authStatus) {
			case "Authenticated":
				return "Logged In";
			case "AuthenticatedButFailing":
				return "Auth Issue";
			default:
				return "Logged Out";
		}
	};

	const reset = () => {
		// Do not interrupt an interactive provider login on Spark reselection. Detach it
		// from UI ownership; its completion may reconcile the replacement requirements.
		// Pending URL/status requests can be released immediately and cannot mutate reset state.
		for (const attempt of activeLogins.values()) {
			if (!attempt.closePopup) attempt.cancel();
		}
		activeLogins.clear();
		platformErrors.value = {};
		platformLoginPending.value = {};
		platformRequirements.value = [];
		platformBusy.value = {};
		platformsLoaded.value = false;
		platformsStale.value = false;
		allPlatformsList.value = [];
		platformsLoadGeneration++;
		latestPlatformRequest = null;
		liveRefreshInFlight = null;
		liveRefreshToken = null;
		releasePlatformsLoadWaiters?.();
	};

	return {
		platformRequirements,
		platformBusy,
		platformErrors,
		platformLoginPending,
		cancelPlatformLogin,
		hasPlatformRequirements,
		allPlatformsAuthenticated,
		allPlatformsList,
		computePlatformRequirements,
		refreshPlatformAuthStatus,
		loginPlatform,
		logoutPlatform: handleLogoutPlatform,
		loadPlatformsIfNeeded,
		getPlatformAuthIcon,
		getPlatformAuthColor,
		getPlatformAuthLabel,
		reset,
	};
}
